import React, { useEffect } from "react";

import { clearEntity } from "EntitiesApi/Actions";
import BoltIcon from "Intuit/BoltIcon";
import HarmonyIcon from "Intuit/HarmonyIcon";
import isIntuit from "Intuit/isIntuit";
import { useSelector, connect } from "react-redux";
import { reduxForm, FieldArray, Field, formValueSelector, touch } from "redux-form";
import { validateRequired, validateDate, validateBeforeToday, validateDependentAge } from "Utils/Validations";
import BasicLoading from "ZeroRuby/BasicLoading";

import { SurfaceHeader, SurfaceSection } from "Cobalt/Surface";
import AgentHeader from "Common/Agent/AgentHeader";
import BodyText from "Common/BodyText";
import Button from "Common/Button";
import { SIFormField, SIFormSelect, SIFormInput } from "Common/forms";
import { dateMask } from "Common/forms/masks";
import { formFormatDate, formParseDate } from "Common/forms/utils";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";
import RemoveIcon from "Common/RemoveIcon";
import { SizeSwitch } from "Common/Responsive";
import { Table } from "Common/Table";

import { stepCompleted } from "./actions";
import { useUpdateFamilyInformationMutation, useGetApplicationQuery, getEmployeeForm, getDependents } from "./api";
import FooterNavigation from "./FooterNavigation";
import Page from "./Page";
import { getEmployeeDependentsInProgress } from "./selectors";

import type { EmployeeApplicationState } from "EmployeeApplication/types";
import type { EmployeeDependent } from "Utils/types";

type Props = {
  member: string;
  partnerIndex: number;
  index: number;
  fields: any;
  memberData: any;
  touch: any;
};

const validateBornAt = (value, allValues, props, name): string | null | undefined => {
  const result = [validateRequired(value), validateDate(value), validateBeforeToday(value)];

  // name comes in as dependents[0].bornAt, this will get us to just the 0
  const index = Number(name.split("[")[1].split("]")[0]);

  if (allValues.dependents?.length > 0 && allValues.dependents[index] && allValues.dependents[index].dependentType === "child" && props) {
    result.push(validateDependentAge(value));
  }

  return result.join("").length > 0 ? result.join("") : undefined;
};

const DependentRowDumb = ({ member, index, partnerIndex, fields, memberData, touch }: Props) => {
  useEffect(() => {
    // The child age validation is kind of flaky. This ensures that if the bornAt value is invalid the cell will be highlighted red.
    if (memberData.bornAt) {
      touch("familyInformation", `${member}.bornAt`);
    }
  }, []);

  return (
    <tr key={index}>
      <td>
        <Field name={`${member}.firstName`} component={SIFormInput} validate={validateRequired} data-testid={`${member}.firstName`} />
      </td>
      <td>
        <Field name={`${member}.lastName`} component={SIFormInput} validate={validateRequired} data-testid={`${member}.lastName`} />
      </td>
      <td>
        <Field name={`${member}.bornAt`} component={SIFormInput} props={{ mask: dateMask }} validate={validateBornAt} data-testid={`${member}.bornAt`} />
      </td>
      <td>
        <Field
          name={`${member}.gender`}
          component={SIFormSelect}
          props={{
            options: [
              ["male", "Male"],
              ["female", "Female"],
            ],
          }}
          validate={[validateRequired]}
          data-testid={`${member}.gender`}
        />
      </td>
      <td>
        <Field
          name={`${member}.dependentType`}
          component={SIFormSelect}
          props={{
            options:
              partnerIndex === index || partnerIndex === -1
                ? [
                    ["spouse", "Spouse"],
                    ["domestic_partner", "Domestic Partner"],
                    ["child", "Child"],
                  ]
                : [["child", "Child"]],
          }}
          validate={[validateRequired]}
          data-testid={`${member}.dependentType`}
        />
      </td>
      <td>
        <Button color="link" onClick={() => fields.remove(index)}>
          {isIntuit() ? <BoltIcon icon="delete" /> : <RemoveIcon />}
        </Button>
      </td>
    </tr>
  );
};
const DependentsList = ({ dependentInProgress, fields }): React.ReactElement<any> => {
  const partnerIndex = dependentInProgress.findIndex((f) => f.dependentType === "spouse" || f.dependentType === "domestic_partner");

  return (
    <div style={{ minHeight: 300 }}>
      <Table>
        <thead>
          <tr>
            <th style={{ textAlign: "left", width: "18%" }}>First Name</th>
            <th style={{ textAlign: "left", width: "18%" }}>Last Name</th>
            <th style={{ textAlign: "left", width: "18%" }}>Date of Birth</th>
            <th style={{ textAlign: "left", width: "18%" }}>Gender</th>
            <th style={{ textAlign: "left", width: "18%" }}>Relationship</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {fields.map((member, index) => (
            <DependentRow key={index} fields={fields} member={member} index={index} partnerIndex={partnerIndex} />
          ))}
        </tbody>
      </Table>
      <div>
        <Button
          color="gray"
          size="small"
          onClick={() =>
            fields.push({
              type: fields.length > 0 ? "child" : undefined,
            })
          }
        >
          {isIntuit() ? <HarmonyIcon icon="create-lg" /> : <Icon icon="plus" />}
          {" Add a Family Member"}
        </Button>
      </div>
    </div>
  );
};

// TODO - Ariana - threw together this mobile version during open enrollment, but it could
// use cleanup/refactoring to reduce duplication and maybe be moved to a different file
const DependentsListMobile = ({ dependentInProgress, fields }): React.ReactElement<any> => {
  const partnerIndex = dependentInProgress.findIndex((f) => f.dependentType === "spouse" || f.dependentType === "domestic_partner");

  return (
    <div style={{ minHeight: 300 }}>
      <Row className="flex-col md:flex-row">
        {fields.map((member, index) => (
          <div key={index}>
            <Row mx={4} className="justify-between">
              <BodyText size="large" weight="bold">
                Dependent {index + 1}
              </BodyText>
              <Button px={2} color="link" onClick={() => fields.remove(index)}>
                {isIntuit() ? <BoltIcon icon="delete" /> : <RemoveIcon />}
              </Button>
            </Row>
            <SIFormField label="First Name" name={`${member}.firstName`} component={SIFormInput} validate={validateRequired} />

            <SIFormField label="Last Name" name={`${member}.lastName`} component={SIFormInput} validate={validateRequired} />

            <SIFormField label="Date of Birth" name={`${member}.bornAt`} component={SIFormInput} props={{ mask: dateMask }} validate={validateBornAt} />

            <SIFormField
              label="Gender"
              name={`${member}.gender`}
              component={SIFormSelect}
              props={{
                options: [
                  ["male", "Male"],
                  ["female", "Female"],
                ],
              }}
              validate={[validateRequired]}
            />

            <SIFormField
              label="Relationship"
              name={`${member}.dependentType`}
              component={SIFormSelect}
              props={{
                options:
                  partnerIndex === index || partnerIndex === -1
                    ? [
                        ["spouse", "Spouse"],
                        ["domestic_partner", "Domestic Partner"],
                        ["child", "Child"],
                      ]
                    : [["child", "Child"]],
              }}
              validate={[validateRequired]}
            />
          </div>
        ))}
      </Row>
      <div>
        <Button
          color="gray"
          size="small"
          onClick={() =>
            fields.push({
              type: fields.length > 0 ? "child" : undefined,
            })
          }
        >
          {isIntuit() ? <HarmonyIcon icon="create-lg" /> : <Icon icon="plus" />}
          {" Add a Family Member"}
        </Button>
      </div>
    </div>
  );
};

type DumbProps = {
  backLink: string;
  onClickBack: () => void;
};

const FamilyInformationPage = ({ backLink, onClickBack }: DumbProps) => {
  const { isLoading } = useGetApplicationQuery();
  const [updateFamilyInformation] = useUpdateFamilyInformationMutation();
  const employeeForm = useSelector(getEmployeeForm);
  const dependents = useSelector(getDependents);

  const initialValues = {
    dependents: dependents?.map(formFormatEmployeeDependent),
  };

  return (
    <Page title="Family Information">
      {isLoading ? (
        <BasicLoading />
      ) : (
        <FamilyInformationForm
          backLink={backLink}
          onClickBack={onClickBack}
          employeeFormId={employeeForm.id}
          initialValues={initialValues}
          updateFamilyInformation={updateFamilyInformation}
        />
      )}
    </Page>
  );
};

const formSelector = formValueSelector("familyInformation");
const formFormatEmployeeDependent = (employee: EmployeeDependent | null | undefined) =>
  employee
    ? {
        ...employee,
        bornAt: employee.bornAt ? formFormatDate(new Date(employee.bornAt)) : null,
      }
    : {};

const formParseEmployeeDependent = (values: any): Partial<EmployeeDependent> => ({
  ...values,
  bornAt: formParseDate(values.bornAt),
});

const withReduxForm = reduxForm({
  form: "familyInformation",
  enableReinitialize: true,
  onSubmit: (values, dispatch, props) => {
    // Keep employee entities up to date until app is fully converted to use rtk
    dispatch(clearEntity("employeeDependents"));
    dispatch(clearEntity("employeeForms"));
    return props
      .updateFamilyInformation({
        employee_dependents: values.dependents.map(formParseEmployeeDependent),

        id: props.employeeFormId,
      })
      .unwrap(); // no-op beyond ensuring/awaiting success
  },
  onSubmitSuccess: (_result, dispatch) => dispatch(stepCompleted("familyInformation")),
});

const mapStateToPropsDepedentRow = (state: EmployeeApplicationState, ownProps) => {
  const member = formSelector(state, ownProps.member);
  const { dependentType } = member;
  return {
    memberData: member,
    dependentType,
  };
};

const mapDispatchToPropsDependenRow = {
  touch,
};

const withReduxDepedentRow = connect(mapStateToPropsDepedentRow, mapDispatchToPropsDependenRow);

const DependentRow = withReduxDepedentRow(DependentRowDumb);

const FamilyInformationForm = withReduxForm(({ handleSubmit, valid, backLink, onClickBack }) => {
  const dependentInProgress = useSelector<EmployeeApplicationState>((state) => getEmployeeDependentsInProgress(state));

  return (
    <form onSubmit={handleSubmit}>
      <SurfaceHeader>Add Family Members</SurfaceHeader>
      <SurfaceSection>
        <AgentHeader>
          We need to know who is in your family and their ages so we can provide accurate prices. Add everyone in your family, even if they won't be getting
          insurance through your plan.
        </AgentHeader>
      </SurfaceSection>
      <SurfaceSection>
        <SizeSwitch
          md={<FieldArray name="dependents" component={DependentsList} valid={valid} dependentInProgress={dependentInProgress} />}
          xs={<FieldArray name="dependents" component={DependentsListMobile} valid={valid} dependentInProgress={dependentInProgress} />}
        />
      </SurfaceSection>
      <FooterNavigation backLink={backLink} onClickBack={onClickBack} saveDisabled={!valid} />
    </form>
  );
});

export default FamilyInformationPage;
